// DEPLOY-PROBE-bump

/**
 * Unified list of office employees and construction workers (full admin only).
 * One row per empCode; 従業員 may exist in both systems.
 */

import React from 'react';

export interface UnifiedUserRow {
  empCode: string;
  name: string;
  roleLabel: string;
  /** 担当現場管理者: assigned genba from API (office_admin_assigned_genba). */
  assignedGenbaName?: string;
  hasOffice: boolean;
  hasConstruction: boolean;
}

interface UnifiedUserSearchResultsProps {
  rows: UnifiedUserRow[];
  searchQuery: string;
  isSearching: boolean;
  onEditOffice: (empCode: string) => void;
  onEditConstruction: (empCode: string) => void;
  onDeleteOffice: (empCode: string) => void;
  onDeleteConstruction: (empCode: string) => void;
  /** 現場管理者: copy scoped to 現場のみ */
  constructionAdminOnly?: boolean;
}

const ASSIGNED_SITE_ADMIN = '担当現場管理者';

/** Badge style: employee blue; 管理者 dark red; 現場系管理者 lighter red */
function getUnifiedRoleBadgeClass(roleLabel: string): string {
  const t = roleLabel.trim();
  if (t === '管理者' || t === '管理者（上位権限）') return 'unified-role-admin';
  if (t === '現場管理者' || t === '管理者（現場権限）' || t === ASSIGNED_SITE_ADMIN) return 'unified-role-admin-site';
  return 'unified-role-employee';
}

function formatUnifiedRoleDisplay(roleLabel: string, assignedGenbaName?: string): string {
  const t = roleLabel.trim();
  if (t === '管理者（上位権限）' || t === '管理者（現場権限）') return t;
  if (t === '管理者') return '管理者（上位権限）';
  if (t === '現場管理者') return '管理者（現場権限）';
  if (t === '現場作業員') return '従業員';
  if (t === ASSIGNED_SITE_ADMIN) {
    const g = assignedGenbaName?.trim();
    return g ? `${ASSIGNED_SITE_ADMIN}（${g}）` : ASSIGNED_SITE_ADMIN;
  }
  return t;
}

export const UnifiedUserSearchResults: React.FC<UnifiedUserSearchResultsProps> = ({
  rows,
  searchQuery,
  isSearching,
  onEditOffice,
  onEditConstruction,
  onDeleteOffice,
  onDeleteConstruction,
  constructionAdminOnly = false,
}) => {
  const handleEdit = (row: UnifiedUserRow, e?: React.MouseEvent | React.TouchEvent) => {
    if (e) e.stopPropagation();
    if (row.hasOffice) onEditOffice(row.empCode);
    else onEditConstruction(row.empCode);
  };

  const handleDelete = (row: UnifiedUserRow, e?: React.MouseEvent | React.TouchEvent) => {
    if (e) e.stopPropagation();
    if (row.hasOffice) onDeleteOffice(row.empCode);
    else onDeleteConstruction(row.empCode);
  };

  if (isSearching) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">⏳</div>
        <p>検索中...</p>
      </div>
    );
  }

  if (rows.length > 0) {
    return (
      <div className="results-content">
        <div className="results-header">
          <h4>一覧 ({rows.length}件)</h4>
        </div>
        <div className="results-table">
          <table className="user-mgmt-unified-table">
            <thead>
              <tr>
                <th>従業員ID</th>
                <th>名前</th>
                <th className="unified-col-role">権限・種別</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.empCode}>
                  <td>{row.empCode}</td>
                  <td>{row.name}</td>
                  <td className="unified-col-role">
                    <span
                      className={`unified-role-badge ${getUnifiedRoleBadgeClass(row.roleLabel)}`}
                    >
                      {formatUnifiedRoleDisplay(row.roleLabel, row.assignedGenbaName)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-small">
                      <button
                        type="button"
                        className="edit-button"
                        title="編集"
                        onClick={(e) => handleEdit(row, e)}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          setTimeout(() => handleEdit(row, e), 0);
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="delete-button"
                        title="削除"
                        onClick={(e) => handleDelete(row, e)}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          setTimeout(() => handleDelete(row, e), 0);
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (searchQuery.trim() && !isSearching) {
    return (
      <div className="no-results">
        <div className="no-results-icon">🔍</div>
        <p>検索条件に一致する従業員が見つかりませんでした</p>
        <p className="no-results-hint">別の検索条件を試してください</p>
      </div>
    );
  }

  if (!searchQuery.trim() && !isSearching) {
    return (
      <div className="welcome-message">
        <div className="welcome-icon">👥</div>
        <p>
          {constructionAdminOnly
            ? '現場の従業員を一覧で確認できます。検索または新規追加をご利用ください。'
            : '本社・現場の従業員を一覧で確認できます。検索または新規追加をご利用ください。'}
        </p>
        <p className="welcome-hint">現在の登録一覧を表示しています。</p>
      </div>
    );
  }

  return null;
};
